import type { SingleValue } from 'react-select';

import {
  Badge,
  RailModalDivider,
  RailModalRail,
  RailModalRailItem,
  Select,
  Text,
} from '@revelio/ui';

import styles from '../preferences-modal.module.css';
import {
  PreferenceScope,
  SCOPED_PREFERENCE_KEYS,
  type UserPreferenceKey,
  type UserPreferenceSubKey,
} from '../types';
import type { PreferenceEditorEntry } from './preferenceEditors';

const SCOPE_LABELS: Record<string, string> = {
  [PreferenceScope.COMPANY]: 'Company',
  [PreferenceScope.INDUSTRY]: 'Industry',
  [PreferenceScope.GEOGRAPHY]: 'Geography',
  [PreferenceScope.ROLE]: 'Role',
  [PreferenceScope.SKILL]: 'Skill',
  [PreferenceScope.ACTIVITY]: 'Activity',
};

type ScopeOption = { value: PreferenceScope; label: string };

export interface PreferencesRailProps {
  editors: readonly PreferenceEditorEntry[];
  activeId?: UserPreferenceKey;
  onSelect: (id: UserPreferenceKey) => void;
  diff: (UserPreferenceKey | UserPreferenceSubKey)[];
  hasError: boolean;
  /** Scopes offered by the switcher (COMPANY / GEOGRAPHY / ROLE). */
  scopeOptions: PreferenceScope[];
  selectedScope: PreferenceScope;
  onScopeChange: (scope: PreferenceScope) => void;
}

const partition = (editors: readonly PreferenceEditorEntry[]) => {
  const global: PreferenceEditorEntry[] = [];
  const scoped: PreferenceEditorEntry[] = [];
  for (const entry of editors) {
    (SCOPED_PREFERENCE_KEYS.includes(entry.meta.id) ? scoped : global).push(
      entry
    );
  }
  return { global, scoped };
};

/**
 * Preference categories rail: the global settings, then — when the page exposes
 * any scope-specific settings — a divider, a Company/Geography/Role scope
 * dropdown, and the scoped settings. The dropdown lets the modal edit any
 * primary scope from any page, so its layout is identical everywhere.
 */
export const PreferencesRail = ({
  editors,
  activeId,
  onSelect,
  diff,
  hasError,
  scopeOptions,
  selectedScope,
  onScopeChange,
}: PreferencesRailProps) => {
  const { global, scoped } = partition(editors);

  if (editors.length === 0) {
    return <Text>No preferences available</Text>;
  }

  // A compact dropdown (not a segmented control) so three scope names never
  // widen the rail. As a combobox it also can't collide with the "Geography"
  // preference option in button/text queries.
  const scopeSwitcher = scoped.length > 0 && scopeOptions.length > 0 && (
    <div className={styles.scopeSwitcher}>
      <Select<ScopeOption>
        size="sm"
        aria-label="Preference scope"
        data-testid="preferences-scope-switcher"
        isSearchable={false}
        isClearable={false}
        value={{
          value: selectedScope,
          label: SCOPE_LABELS[selectedScope] ?? selectedScope,
        }}
        options={scopeOptions.map((s) => ({
          value: s,
          label: SCOPE_LABELS[s] ?? s,
        }))}
        onChange={(opt: SingleValue<ScopeOption>) => {
          if (opt) onScopeChange(opt.value);
        }}
      />
    </div>
  );

  const renderItem = ({ meta }: PreferenceEditorEntry) => {
    const hasDifference = !hasError && diff.includes(meta.id);
    return (
      <RailModalRailItem
        key={meta.id}
        isActive={meta.id === activeId}
        onClick={() => onSelect(meta.id)}
      >
        {meta.label}
        {hasDifference && (
          <Badge
            className={styles.diffDot}
            size="sm"
            data-testid="preference-diff-dot"
          />
        )}
      </RailModalRailItem>
    );
  };
  return (
    <RailModalRail
      aria-label="Preference categories"
      data-testid="user-preferences-sidebar"
    >
      {global.map(renderItem)}
      {scoped.length > 0 && (
        <>
          <RailModalDivider className={styles.railDivider} />
          {scopeSwitcher}
          {scoped.map(renderItem)}
        </>
      )}
    </RailModalRail>
  );
};
